import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Loader2, Scissors, Play, Pause, SkipBack, SkipForward, Check, Clock } from "lucide-react";

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const VideoTrimmer = ({ open, onClose, videoSrc, onTrimComplete, maxDuration = 60 }) => {
  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(null); // 'start', 'end', 'playhead'

  // Video yüklendiğinde
  useEffect(() => {
    if (videoSrc && videoRef.current) {
      const video = videoRef.current;
      setIsLoaded(false);
      
      const handleLoadedMetadata = () => {
        const dur = video.duration;
        if (dur && !isNaN(dur)) {
          setDuration(dur);
          setEndTime(Math.min(dur, maxDuration));
          setStartTime(0);
          setCurrentTime(0);
          setIsLoaded(true);
        }
      };
      
      const handleTimeUpdate = () => {
        setCurrentTime(video.currentTime);
        if (video.currentTime >= endTime) {
          video.pause();
          setIsPlaying(false);
          video.currentTime = startTime;
        }
      };

      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('timeupdate', handleTimeUpdate);
      
      // Video zaten yüklüyse
      if (video.readyState >= 1) {
        handleLoadedMetadata();
      }

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, [videoSrc, maxDuration]);

  // endTime değiştiğinde kontrol et
  useEffect(() => {
    if (videoRef.current && currentTime >= endTime) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [endTime, currentTime]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        if (videoRef.current.currentTime < startTime || videoRef.current.currentTime >= endTime) {
          videoRef.current.currentTime = startTime;
        }
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Timeline üzerinde tıklama/sürükleme
  const getTimeFromPosition = (e) => {
    if (!timelineRef.current || !duration) return 0;
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    return percentage * duration;
  };

  const handleTimelineMouseDown = (e, type) => {
    e.preventDefault();
    setIsDragging(type);
    
    const handleMouseMove = (moveEvent) => {
      const time = getTimeFromPosition(moveEvent);
      
      if (type === 'start') {
        const newStart = Math.max(0, Math.min(time, endTime - 1));
        setStartTime(newStart);
        if (videoRef.current) {
          videoRef.current.currentTime = newStart;
        }
      } else if (type === 'end') {
        const maxEnd = Math.min(duration, startTime + maxDuration);
        const newEnd = Math.max(startTime + 1, Math.min(time, maxEnd));
        setEndTime(newEnd);
      } else if (type === 'playhead') {
        if (videoRef.current) {
          videoRef.current.currentTime = time;
          setCurrentTime(time);
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // İlk tıklamada da güncelle
    handleMouseMove(e);
  };

  // Touch desteği
  const handleTimelineTouchStart = (e, type) => {
    e.preventDefault();
    setIsDragging(type);
    
    const handleTouchMove = (moveEvent) => {
      const touch = moveEvent.touches[0];
      const time = getTimeFromPosition(touch);
      
      if (type === 'start') {
        const newStart = Math.max(0, Math.min(time, endTime - 1));
        setStartTime(newStart);
        if (videoRef.current) {
          videoRef.current.currentTime = newStart;
        }
      } else if (type === 'end') {
        const maxEnd = Math.min(duration, startTime + maxDuration);
        const newEnd = Math.max(startTime + 1, Math.min(time, maxEnd));
        setEndTime(newEnd);
      } else if (type === 'playhead') {
        if (videoRef.current) {
          videoRef.current.currentTime = time;
          setCurrentTime(time);
        }
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(null);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    
    // İlk dokunuşta da güncelle
    handleTouchMove(e);
  };

  const trimVideo = async () => {
    setProcessing(true);
    try {
      const trimInfo = { 
        startTime, 
        endTime, 
        duration: endTime - startTime 
      };
      onTrimComplete(videoSrc, trimInfo);
      onClose();
    } catch (error) {
      console.error("Video trim error:", error);
      onTrimComplete(videoSrc, { startTime, endTime, duration: endTime - startTime });
      onClose();
    } finally {
      setProcessing(false);
    }
  };

  const selectedDuration = endTime - startTime;
  const isValidDuration = selectedDuration >= 1 && selectedDuration <= maxDuration;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5 text-emerald-600" />
            Video Kes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Video Preview */}
          <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
            <video
              ref={videoRef}
              src={videoSrc}
              className="w-full h-full object-contain"
              playsInline
              preload="metadata"
            />
            
            {/* Loading Overlay */}
            {!isLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <Loader2 className="h-8 w-8 text-white animate-spin" />
              </div>
            )}
            
            {/* Play/Pause Overlay */}
            {isLoaded && (
              <button
                onClick={handlePlayPause}
                className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition"
              >
                {!isPlaying && (
                  <div className="bg-black/50 rounded-full p-4">
                    <Play className="h-8 w-8 text-white" />
                  </div>
                )}
              </button>
            )}
            
            {/* Time Display */}
            <div className="absolute bottom-2 left-2 bg-black/70 text-white text-sm px-2 py-1 rounded">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          {/* Timeline Control */}
          {isLoaded && duration > 0 && (
            <div className="space-y-3">
              {/* Ana Timeline */}
              <div 
                ref={timelineRef}
                className="relative h-16 bg-gray-200 rounded-lg overflow-hidden cursor-pointer select-none"
                onMouseDown={(e) => handleTimelineMouseDown(e, 'playhead')}
                onTouchStart={(e) => handleTimelineTouchStart(e, 'playhead')}
              >
                {/* Seçili Alan */}
                <div
                  className="absolute top-0 bottom-0 bg-emerald-200"
                  style={{
                    left: `${(startTime / duration) * 100}%`,
                    width: `${((endTime - startTime) / duration) * 100}%`
                  }}
                />
                
                {/* Başlangıç Handle */}
                <div
                  className="absolute top-0 bottom-0 w-4 bg-emerald-600 cursor-ew-resize flex items-center justify-center z-20 hover:bg-emerald-700 transition-colors"
                  style={{ left: `calc(${(startTime / duration) * 100}% - 8px)` }}
                  onMouseDown={(e) => { e.stopPropagation(); handleTimelineMouseDown(e, 'start'); }}
                  onTouchStart={(e) => { e.stopPropagation(); handleTimelineTouchStart(e, 'start'); }}
                >
                  <div className="w-1 h-8 bg-white rounded" />
                </div>
                
                {/* Bitiş Handle */}
                <div
                  className="absolute top-0 bottom-0 w-4 bg-emerald-600 cursor-ew-resize flex items-center justify-center z-20 hover:bg-emerald-700 transition-colors"
                  style={{ left: `calc(${(endTime / duration) * 100}% - 8px)` }}
                  onMouseDown={(e) => { e.stopPropagation(); handleTimelineMouseDown(e, 'end'); }}
                  onTouchStart={(e) => { e.stopPropagation(); handleTimelineTouchStart(e, 'end'); }}
                >
                  <div className="w-1 h-8 bg-white rounded" />
                </div>
                
                {/* Playhead */}
                <div
                  className="absolute top-0 bottom-0 w-1 bg-red-500 z-30"
                  style={{ left: `${(currentTime / duration) * 100}%` }}
                >
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-red-500 rounded-full" />
                </div>
                
                {/* Zaman İşaretleri */}
                <div className="absolute bottom-0 left-0 right-0 flex justify-between px-2 text-xs text-gray-600">
                  <span>{formatTime(0)}</span>
                  <span>{formatTime(duration / 2)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Başlangıç/Bitiş Bilgisi */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
                  <SkipBack className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">Başlangıç:</span>
                  <span className="text-emerald-700 font-bold">{formatTime(startTime)}</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
                  <SkipForward className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">Bitiş:</span>
                  <span className="text-emerald-700 font-bold">{formatTime(endTime)}</span>
                </div>
              </div>

              {/* Duration Info */}
              <div className={`flex items-center justify-center gap-2 p-3 rounded-lg ${isValidDuration ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                <Clock className="h-4 w-4" />
                <span className="font-medium">
                  Seçili süre: {formatTime(selectedDuration)} 
                  {selectedDuration > maxDuration && ` (Max ${maxDuration}sn)`}
                </span>
              </div>

              {/* Playback Controls */}
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (videoRef.current) {
                      videoRef.current.currentTime = startTime;
                      setCurrentTime(startTime);
                    }
                  }}
                >
                  <SkipBack className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePlayPause}
                >
                  {isPlaying ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                  {isPlaying ? "Duraklat" : "Oynat"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (videoRef.current) {
                      videoRef.current.currentTime = endTime;
                      setCurrentTime(endTime);
                    }
                  }}
                >
                  <SkipForward className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={processing}>
            İptal
          </Button>
          <Button
            onClick={trimVideo}
            disabled={processing || !isValidDuration || !isLoaded}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                İşleniyor...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Kes ve Kaydet
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VideoTrimmer;
